// src/pages/RankingPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import { useAuthCliente } from '../context/AuthClienteContext';
import { db } from '../firebase/firebase-config';
import { collection, query, onSnapshot } from 'firebase/firestore';
import {
  Box, Heading, VStack, Text, Spinner, Center, Alert, AlertIcon,
  HStack, useDisclosure, Container, SimpleGrid, Avatar,
  Icon, Table, Thead, Tbody, Tr, Th, Td, TableContainer, IconButton, Tag
} from '@chakra-ui/react';
import PlayerProfileModal from '../components/PlayerProfileModal';
import { FaTrophy, FaArrowUp, FaArrowDown, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

// --- ✅ TABLA DE RANKING MODIFICADA PARA AÑADIR MÁS COLUMNAS ---
function FullRankingTable({ members, onViewProfile }) {
  return (
    <TableContainer bg="white" borderRadius="lg" boxShadow="md" borderWidth="1px" borderColor="gray.200">
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>#</Th>
            <Th>Jugador</Th>
            <Th>Categoría</Th>
            <Th>Sexo</Th>
            <Th isNumeric>Edad</Th>
            <Th isNumeric>Puntos</Th>
            <Th textAlign="center">Cambio</Th>
          </Tr>
        </Thead>
        <Tbody>
          {members.map((member) => {
            // Lógica para calcular edad y sexo
            const birth = member.birthDate ? new Date(member.birthDate) : null;
            const age = birth ? Math.floor((Date.now() - birth.getTime()) / (365.25 * 24 * 60 * 60 * 1000)) : 'N/A';
            const sexoLabel = member.sexo?.startsWith('f') ? 'Femenino' : (member.sexo?.startsWith('m') ? 'Masculino' : 'N/A');

            return (
              <Tr 
                key={member.id} 
                _hover={{ bg: 'green.600', color: 'white', cursor: 'pointer' }}
                onClick={() => onViewProfile(member)}
              >
                <Td fontWeight="bold">{member.rank}</Td>
                <Td>
                  <HStack>
                    <Avatar size="sm" name={member.name} src={member.photoURL} />
                    <Text>{member.name}</Text>
                  </HStack>
                </Td>
                {/* Nuevas Columnas de Datos */}
                <Td>{member.category || 'N/A'}</Td>
                <Td>{sexoLabel}</Td>
                <Td isNumeric>{age}</Td>

                <Td isNumeric fontWeight="bold" color="brand.600">{member.points || 0}</Td>
                <Td textAlign="center">
                  {member.change === 'up' && <Icon as={FaArrowUp} color="green.500" />}
                  {member.change === 'down' && <Icon as={FaArrowDown} color="red.500" />}
                  {member.change === 'same' && <Text color="gray.400">-</Text>}
                  {member.change === 'new' && <Tag size="sm" colorScheme="blue">Nuevo</Tag>}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
}


export default function RankingPage() {
  const { currentUser } = useAuthCliente();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedMember, setSelectedMember] = useState(null);

  const [tablePage, setTablePage] = useState(0);
  const itemsPerTablePage = 10;
  
  const previousMembersRef = useRef([]);

  useEffect(() => {
    if (!currentUser?.companyId) {
      setLoading(false);
      return;
    }
    const ref = collection(db, 'companies', currentUser.companyId, 'end_customers');
    const q = query(ref);

    const unsub = onSnapshot(q, snap => {
      const newList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      newList.sort((a, b) => (b.points || 0) - (a.points || 0) || a.name.localeCompare(b.name));

      const membersWithChanges = newList.map((currentMember, newIndex) => {
        const oldIndex = previousMembersRef.current.findIndex(oldMember => oldMember.id === currentMember.id);
        let change = 'same';
        if (oldIndex === -1) {
          change = 'new';
        } else if (newIndex < oldIndex) {
          change = 'up';
        } else if (newIndex > oldIndex) {
          change = 'down';
        }
        return { ...currentMember, change, rank: newIndex + 1 };
      });
      
      setMembers(membersWithChanges);
      previousMembersRef.current = newList; 
      setLoading(false);
    }, err => {
      setError('No se pudo cargar la lista de miembros.');
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser?.companyId]);

  const handleViewProfile = member => {
    setSelectedMember(member);
    onOpen();
  };

  const top10 = members.slice(0, 10);

  const totalTablePages = Math.ceil(members.length / itemsPerTablePage);
  const paginatedMembers = members.slice(tablePage * itemsPerTablePage, (tablePage + 1) * itemsPerTablePage);

  if (loading) return <Center p={10}><Spinner size="xl" /></Center>;
  if (error) return <Alert status="error"><AlertIcon />{error}</Alert>;

  return (
    <>
      <PlayerProfileModal isOpen={isOpen} onClose={onClose} member={selectedMember} />
      
      <Container maxW="container.xl" py={8}>
        <VStack spacing={12} align="stretch">

          <VStack spacing={10} align="stretch">
            <Box>
              <HStack justifyContent="space-between" mb={4}>
                <HStack spacing={3} align="center">
                  <Icon as={FaTrophy} w={8} h={8} color="yellow.400" />
                  <Heading size="xl">Rankings del Club</Heading>
                </HStack>
              </HStack>
              <Text color="gray.600">Consulta tu posición y la de tus amigos.</Text>
            </Box>

            <Box>
              <Heading size="lg" mb={4}>Top 10 - Singles</Heading>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={6}>
                {top10.map((m, idx) => (
                  <VStack
                    key={m.id}
                    p={4}
                    bg="white"
                    borderRadius="lg"
                    boxShadow="md"
                    cursor="pointer"
                    _hover={{ bg: 'green.600', color: 'white', transform: 'translateY(-4px)', shadow: 'lg' }}
                    transition="all 0.5s"
                    onClick={() => handleViewProfile(m)}
                  >
                    <Text fontWeight="bold" fontSize="xl" color="gray.400">#{idx+1}</Text>
                    <Avatar size="lg" name={m.name} src={m.photoURL} />
                    <Text fontWeight="bold" textAlign="center">{m.name}</Text>
                    <Text fontSize="sm" color="brand.600">{m.points || 0} pts</Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </Box>
          </VStack>
          
          <Box>
            <Heading size="lg" mb={4}>Ranking Completo</Heading>
            {members.length > 0 ? (
              <VStack align="stretch" spacing={4}>
                <FullRankingTable members={paginatedMembers} onViewProfile={handleViewProfile} />
                {totalTablePages > 1 && (
                  <HStack justify="center">
                    <IconButton icon={<FaArrowLeft />} aria-label="Anterior" onClick={() => setTablePage(p => p - 1)} isDisabled={tablePage === 0} />
                    <Text fontSize="sm" color="gray.600">{tablePage + 1} / {totalTablePages}</Text>
                    <IconButton icon={<FaArrowRight />} aria-label="Siguiente" onClick={() => setTablePage(p => p + 1)} isDisabled={tablePage >= totalTablePages - 1} />
                  </HStack>
                )}
              </VStack>
            ) : (
              <Text color="gray.500">No hay jugadores registrados en el ranking.</Text>
            )}
          </Box>

        </VStack>
      </Container>
    </>
  );
}